import React, { useState, useContext, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
	MessageSquare,
	Eye,
	EyeOff,
	Mail,
	Lock,
	LogIn,
	Check,
	ShieldCheck,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../lib/utils";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhiteLabel } from "../../context/WhiteLabel";
import { lerPreferencia, gravarPreferencia } from "../../services/preferencias";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";
import api from "../../services/api";
import {
	cotaDoPlano,
	ehSiteDeStatus,
	limiteDoPlano,
	VITRINE_STATUS,
} from "../../helpers/planoStatus";

// Entrada do sistema.
//
// Duas metades: à esquerda o que o produto faz e quanto custa; à direita o
// formulário. Quem já é cliente olha só a direita — por isso o cartão fica do
// lado do polegar em telas grandes e é a única coisa que sobra no celular.
//
// A metade da esquerda existe porque esta URL também recebe quem ainda não
// comprou: o link do login circula, e uma tela só com dois campos não diz a
// essa pessoa o que ela está prestes a assinar.

const CHAVE_EMAIL = "login-email-lembrado";

const BENEFICIOS = [
	{
		titulo: "Todos os canais numa caixa só",
		detalhe:
			"WhatsApp, Facebook Messenger e Instagram Direct chegam no mesmo departamento.",
	},
	{
		titulo: "Automação que atende sozinha",
		detalhe:
			"Responde na hora, qualifica o cliente e encaminha para o setor certo.",
	},
	{
		titulo: "A equipe sob controle",
		detalhe:
			"Departamentos por setor, transferências e relatórios de cada atendimento.",
	},
];

const VITRINE_PADRAO = {
	titulo: "Um só painel.",
	destaque: "Controle total do atendimento.",
	texto:
		"WhatsApp, Facebook e Instagram Direct no mesmo lugar. A automação responde na hora, direciona cada conversa para o setor certo e sua equipe entra só onde precisa fechar a venda.",
	beneficios: BENEFICIOS,
};

// Na moeda em que a plataforma cobra, não no real fixo: a vitrine é a
// primeira coisa que o cliente vê, e o preço errado ali não se desfaz depois.
const dinheiro = (valor) => formatarMoeda(valor, moedaPlataforma());

const Login = () => {
	// Puxa a moeda da plataforma; o formatador acima lê dela.
	useMoedaAtiva();
	// `logoDoTema`: o cartão do login é `bg-card`, que escurece junto com o
	// tema — a logo clara sumia no fundo escuro.
	const { appName, logoDoTema, backgroundUrl } = useWhiteLabel();
	const { handleLogin } = useContext(AuthContext);
	const siteDeStatus = ehSiteDeStatus();
	const vitrine = siteDeStatus ? VITRINE_STATUS : VITRINE_PADRAO;

	const [user, setUser] = useState({
		email: lerPreferencia(CHAVE_EMAIL, "") || "",
		password: "",
		twoFactorToken: "",
	});
	const [showPassword, setShowPassword] = useState(false);
	// Segunda etapa: só aparece depois que a senha conferiu e a conta tem
	// segundo fator ligado. Antes disso pedir o código seria dizer a quem
	// erra a senha se aquele e-mail existe e usa 2FA.
	const [pedindo2FA, setPedindo2FA] = useState(false);
	const [erro2FA, setErro2FA] = useState("");
	const [lembrar, setLembrar] = useState(
		Boolean(lerPreferencia(CHAVE_EMAIL, ""))
	);
	const [enviando, setEnviando] = useState(false);

	const [planos, setPlanos] = useState([]);
	const [inicio, setInicio] = useState(0);

	// Os planos são os mesmos que a tela de cadastro vende. Buscar da API em
	// vez de escrever na mão evita a tela anunciar um preço que já mudou.
	useEffect(() => {
		let vivo = true;

		api
			.get("/auth/public-plans")
			.then(({ data }) => {
				if (!vivo) return;
				// Cada endereço vende o seu produto: status.* só os planos de
				// status, o login principal só os de atendimento.
				// Do mais barato para o mais caro: é a ordem em que se decide.
				const ordenados = (data || [])
					.filter((plano) => Boolean(plano.statusOnly) === siteDeStatus)
					.sort(
					(a, b) => Number(a.value) - Number(b.value)
				);
				setPlanos(ordenados);
			})
			.catch(() => {
				// Sem planos a tela continua servindo para entrar, que é o
				// principal. Não vale mostrar erro a quem só quer fazer login.
			});

		return () => {
			vivo = false;
		};
	}, [siteDeStatus]);

	const handleChangeInput = (e) => {
		setUser({ ...user, [e.target.name]: e.target.value });
	};

	const handlSubmit = async (e) => {
		e.preventDefault();
		setEnviando(true);

		// "Lembrar" guarda só o e-mail, nunca a senha: poupa a digitação de
		// quem entra todo dia sem deixar credencial no navegador.
		gravarPreferencia(CHAVE_EMAIL, lembrar ? user.email : "");

		try {
			await handleLogin(user);
		} catch (err) {
			const codigo = err?.precisa2FA
				? "ERR_2FA_REQUIRED"
				: err?.response?.data?.error;

			if (codigo === "ERR_2FA_REQUIRED") {
				// Senha certa, falta o segundo fator: abre a etapa do código.
				setPedindo2FA(true);
				setErro2FA("");
				setUser((atual) => ({ ...atual, twoFactorToken: "" }));
			} else if (codigo === "ERR_INVALID_2FA_TOKEN") {
				// Código errado ou já expirado. A etapa continua aberta e o
				// campo limpa, senão a pessoa reenvia o mesmo código vencido.
				setErro2FA("Código inválido. Use o código que o app mostra agora.");
				setUser((atual) => ({ ...atual, twoFactorToken: "" }));
			} else {
				// Qualquer outra falha volta para o começo: e-mail e senha.
				setPedindo2FA(false);
				setErro2FA("");
			}
		} finally {
			setEnviando(false);
		}
	};

	const porPagina = 3;
	const podeVoltar = inicio > 0;
	const podeAvancar = inicio + porPagina < planos.length;

	const mover = useCallback(
		(passo) => {
			setInicio((atual) => {
				const novo = atual + passo * porPagina;
				if (novo < 0) return 0;
				if (novo >= planos.length) return atual;
				return novo;
			});
		},
		[planos.length]
	);

	const visiveis = planos.slice(inicio, inicio + porPagina);

	return (
		// A tela inteira é escura por baixo do conteúdo, com ou sem foto: o
		// cartão claro precisa de contraste para não flutuar no nada.
		<div className="relative min-h-dvh w-full overflow-hidden bg-slate-950">
			{backgroundUrl ? (
				<>
					<img
						src={backgroundUrl}
						alt=""
						aria-hidden="true"
						className="absolute inset-0 h-full w-full object-cover"
					/>
					{/* Véu por cima da foto: sem ele, texto branco sobre uma
					    imagem clara fica ilegível — e a foto é escolhida pelo
					    cliente, então não dá para contar com ela ser escura. */}
					<div className="absolute inset-0 bg-slate-950/80" />
				</>
			) : (
				<div
					className="absolute inset-0 opacity-70"
					style={{
						backgroundImage:
							"radial-gradient(ellipse 70% 60% at 15% -10%, rgba(37,99,235,0.45), transparent 60%), radial-gradient(ellipse 60% 50% at 100% 110%, rgba(16,185,129,0.35), transparent 60%)",
					}}
				/>
			)}

			<div className="relative z-10 mx-auto flex min-h-dvh max-w-7xl flex-col items-center justify-center gap-10 px-4 py-10 lg:flex-row lg:items-center lg:gap-12 lg:px-8">
				{/* Apresentação — some no celular, onde a tela toda é o
				    formulário e rolar até ele seria um estorvo para quem só
				    quer entrar. */}
				<div className="hidden min-w-0 flex-1 lg:block">
					<h1 className="text-4xl font-bold leading-tight tracking-tight text-white">
						{vitrine.titulo}
						<br />
						<span className="text-emerald-400">{vitrine.destaque}</span>
					</h1>

					<p className="mt-4 max-w-xl text-[15px] leading-relaxed text-slate-300">
						{vitrine.texto}
					</p>

					<ul className="mt-8 space-y-4">
						{vitrine.beneficios.map(({ titulo, detalhe }) => (
							<li key={titulo} className="flex items-start gap-3">
								<span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 ring-1 ring-emerald-400/40">
									<Check className="h-3 w-3 text-emerald-400" />
								</span>
								<div className="min-w-0">
									<p className="text-sm font-semibold text-white">{titulo}</p>
									<p className="mt-0.5 text-sm leading-relaxed text-slate-400">
										{detalhe}
									</p>
								</div>
							</li>
						))}
					</ul>

					{planos.length > 0 && (
						<div className="mt-10">
							<div className="flex items-center justify-between">
								<p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
									Planos
								</p>

								{planos.length > porPagina && (
									<div className="flex items-center gap-1.5">
										<button
											type="button"
											aria-label="Planos anteriores"
											disabled={!podeVoltar}
											onClick={() => mover(-1)}
											className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 ring-1 ring-white/20 transition-colors enabled:hover:bg-white/10 disabled:opacity-30"
										>
											<ChevronLeft className="h-4 w-4" />
										</button>
										<button
											type="button"
											aria-label="Próximos planos"
											disabled={!podeAvancar}
											onClick={() => mover(1)}
											className="flex h-8 w-8 items-center justify-center rounded-full text-slate-300 ring-1 ring-white/20 transition-colors enabled:hover:bg-white/10 disabled:opacity-30"
										>
											<ChevronRight className="h-4 w-4" />
										</button>
									</div>
								)}
							</div>

							<div className="mt-3 grid grid-cols-3 gap-3">
								{visiveis.map((plano, i) => {
									// O mais barato da primeira página é o que a maioria
									// contrata; destacá-lo poupa a comparação.
									const destaque = inicio === 0 && i === 0;

									return (
										<div
											key={plano.id}
											className={cn(
												"relative rounded-xl p-4 ring-1 backdrop-blur-sm",
												destaque
													? "bg-emerald-500/10 ring-emerald-400/40"
													: "bg-white/5 ring-white/10"
											)}
										>
											{destaque && (
												<span className="absolute -top-2 left-4 rounded-full bg-emerald-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-emerald-950">
													Mais popular
												</span>
											)}

											<p className="truncate text-[11px] font-semibold uppercase tracking-wide text-slate-400">
												{plano.name}
											</p>
											<p className="mt-1 text-xl font-bold tabular-nums text-white">
												{dinheiro(plano.value)}
											</p>
											<p className="text-[11px] text-slate-400">por mês</p>

											<ul className="mt-3 space-y-1.5 text-[12px] text-slate-300">
												{siteDeStatus && (
													<li className="flex items-center gap-1.5">
														<Check className="h-3 w-3 shrink-0 text-emerald-400" />
														{cotaDoPlano(plano)}
													</li>
												)}
												<li className="flex items-center gap-1.5">
													<Check className="h-3 w-3 shrink-0 text-emerald-400" />
													{siteDeStatus
														? limiteDoPlano(plano.users, "usuário(s)", "Usuários ilimitados")
														: limiteDoPlano(plano.users, "atendente(s)", "Atendentes ilimitados")}
												</li>
												<li className="flex items-center gap-1.5">
													<Check className="h-3 w-3 shrink-0 text-emerald-400" />
													{limiteDoPlano(plano.connections, "conexão(ões)", "Conexões ilimitadas")}
												</li>
												{/* Plano de status não tem atendimento: departamento
												    ali não quer dizer nada. */}
												{!siteDeStatus && (
													<li className="flex items-center gap-1.5">
														<Check className="h-3 w-3 shrink-0 text-emerald-400" />
														{limiteDoPlano(plano.queues, "departamento(s)", "Departamentos ilimitados")}
													</li>
												)}
											</ul>
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>

				{/* Formulário */}
				<div className="w-full max-w-md shrink-0">
					<div className="rounded-2xl bg-card p-6 shadow-2xl ring-1 ring-black/5 sm:p-8">
						<div className="flex justify-center">
							{logoDoTema ? (
								<img
									src={logoDoTema}
									alt={appName}
									className="h-12 max-w-[180px] object-contain"
								/>
							) : (
								<div className="flex items-center gap-2.5">
									<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary">
										<MessageSquare className="h-6 w-6 text-primary-foreground" />
									</div>
									<span className="text-lg font-bold tracking-tight">
										{appName}
									</span>
								</div>
							)}
						</div>

						<div className="mt-5 flex items-center gap-1.5 text-xs text-muted-foreground">
							<ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
							Área segura
						</div>

						<h2 className="mt-1 text-2xl font-bold tracking-tight">Entrar</h2>

						{/* Duas abas, e a de cadastro leva para outra tela. Fica
						    como aba, e não como link no rodapé, porque quem chega
						    sem conta precisa achar o caminho de primeira. */}
						<div className="mt-5 grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
							<span
								aria-current="page"
								className="rounded-md bg-background px-3 py-2 text-center text-sm font-semibold shadow-sm"
							>
								Entrar
							</span>
							<RouterLink
								to="/signup"
								className="rounded-md px-3 py-2 text-center text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
							>
								Cadastre-se
							</RouterLink>
						</div>

						<form onSubmit={handlSubmit} className="mt-5 space-y-4" noValidate>
							<div className="space-y-1.5">
								<Label htmlFor="email">{i18n.t("login.form.email")}</Label>
								<div className="relative">
									<Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										id="email"
										name="email"
										type="email"
										autoComplete="email"
										autoFocus
										required
										value={user.email}
										onChange={handleChangeInput}
										placeholder="voce@exemplo.com"
										className="h-11 pl-10"
									/>
								</div>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="password">
									{i18n.t("login.form.password")}
								</Label>
								<div className="relative">
									<Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
									<Input
										id="password"
										name="password"
										type={showPassword ? "text" : "password"}
										autoComplete="current-password"
										required
										value={user.password}
										onChange={handleChangeInput}
										placeholder="Sua senha"
										className="h-11 pl-10 pr-11"
									/>
									<button
										type="button"
										tabIndex={-1}
										aria-label={
											showPassword ? "Esconder a senha" : "Mostrar a senha"
										}
										onClick={() => setShowPassword((v) => !v)}
										className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
									>
										{showPassword ? (
											<EyeOff className="h-4 w-4" />
										) : (
											<Eye className="h-4 w-4" />
										)}
									</button>
								</div>
							</div>

							{pedindo2FA && (
								<div className="space-y-1.5">
									<Label htmlFor="twoFactorToken">
										Código de verificação
									</Label>
									<div className="relative">
										<ShieldCheck className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
										<Input
											id="twoFactorToken"
											name="twoFactorToken"
											type="text"
											inputMode="numeric"
											autoComplete="one-time-code"
											maxLength={6}
											autoFocus
											required
											value={user.twoFactorToken}
											onChange={handleChangeInput}
											placeholder="000000"
											className="h-11 pl-10 tracking-[0.35em]"
										/>
									</div>
									<p
										className={cn(
											"text-xs",
											erro2FA ? "text-destructive" : "text-muted-foreground"
										)}
									>
										{erro2FA ||
											"Abra o aplicativo de autenticação e digite os seis dígitos."}
									</p>
								</div>
							)}

							<div className="flex items-center gap-2">
								<Checkbox
									id="lembrar"
									checked={lembrar}
									onCheckedChange={(v) => setLembrar(Boolean(v))}
								/>
								<Label
									htmlFor="lembrar"
									className="cursor-pointer text-sm font-normal text-muted-foreground"
								>
									Lembrar meu e-mail
								</Label>
								<RouterLink
									to="/esqueci-senha"
									className="ml-auto text-sm font-medium text-primary underline-offset-4 hover:underline"
								>
									Esqueci minha senha
								</RouterLink>
							</div>

							<Button
								type="submit"
								disabled={enviando}
								className="h-11 w-full text-[15px] font-semibold"
							>
								<LogIn className="h-4 w-4" />
								{i18n.t("login.buttons.submit")}
							</Button>
						</form>

						<p className="mt-5 text-center text-xs leading-relaxed text-muted-foreground">
							Ao entrar, você concorda com o tratamento dos seus dados conforme
							a LGPD.{" "}
							{/* O link mora aqui porque é onde se procura: quem entra passa
							    por esta frase, e o revisor da Meta abre a tela de login
							    antes de qualquer outra coisa. */}
							<RouterLink
								to="/politica-de-privacidade"
								className="underline underline-offset-4"
							>
								Política de Privacidade
							</RouterLink>
							.
						</p>
					</div>

					{/* No celular a apresentação não aparece; este atalho é o que
					    resta para quem chegou aqui sem conta. */}
					<p className="mt-5 text-center text-sm text-slate-300 lg:hidden">
						Ainda não tem conta?{" "}
						<RouterLink
							to="/signup"
							className="font-semibold text-white underline underline-offset-4"
						>
							Criar agora
						</RouterLink>
					</p>
				</div>
			</div>
		</div>
	);
};

export default Login;
